// Shared color constants + color-vision-deficiency (CVD) schemes.
//
// Chart components read their colors through the useColorScheme() hook
// (src/hooks/useColorScheme.jsx) so the header toggle can swap schemes at
// runtime. The static COLORS / PALETTE / DISTRIBUTION_COLORS exports remain
// for chrome (text, borders) and any legacy imports — they always reflect
// the DEFAULT scheme.
//
// All three palettes were validated with a CVD simulator (OKLab ΔE under
// simulated protanopia/deuteranopia, adjacent-pair standard):
//   default — worst adjacent pair ΔE 23.9 for the primary teal/navy pair
//   deutan  — worst adjacent pair ΔE 12.0 (target ≥ 8)
//   protan  — worst adjacent pair ΔE 9.5, warm hues kept light because
//             protanomalous viewers perceive reds as darker
// Slots 1–2 (brand teal / dark teal) are identical in every scheme — that
// pair separates by lightness, which survives every form of CVD.

// Chrome tokens — identical across schemes
const CHROME = {
  border: '#e2e6ea',
  textPrimary: '#1a2332',
  textSecondary: '#5a6577',
  textMuted: '#8a94a3',
  bgSurface: '#ffffff',
  bgSubtle: '#f7f8fa'
};

// Single-hue teal ramp (dark→light) — lightness-ordered, CVD-safe as-is
const DISTRIBUTION = [
  '#0f4c5c', '#1a6b7a', '#22838f', '#2ec4b6',
  '#5dd4c8', '#8ae0d8', '#b7ece7', '#d4f5f2',
  '#e8f8f7', '#f2fbfa'
];

export const SCHEMES = {
  default: {
    id: 'default',
    label: 'Default',
    teal: '#2ec4b6',
    tealDark: '#0f4c5c',
    coral: '#e07a5f',        // rejection accent (= palette slot 3)
    coralLight: '#e8957e',
    persuadable: '#a9b4bf',
    purple: '#7d3c98',
    orange: '#f4a261',
    navy: '#264653',
    palette: ['#2ec4b6', '#0f4c5c', '#e07a5f', '#7d3c98', '#f4a261', '#264653'],
    distribution: DISTRIBUTION
  },
  deutan: {
    id: 'deutan',
    label: 'Colorblind: deuteranomaly',
    teal: '#2ec4b6',
    tealDark: '#0f4c5c',
    coral: '#e69f00',        // gold rejection accent — clean on the blue/yellow axis
    coralLight: '#f4c26b',
    persuadable: '#a9b4bf',
    purple: '#b0568f',
    orange: '#e69f00',
    navy: '#3f7ad9',
    palette: ['#2ec4b6', '#0f4c5c', '#e69f00', '#b0568f', '#3f7ad9', '#f4c26b'],
    distribution: DISTRIBUTION
  },
  protan: {
    id: 'protan',
    label: 'Colorblind: protanomaly',
    teal: '#2ec4b6',
    tealDark: '#0f4c5c',
    coral: '#f2b544',        // lighter gold — protan viewers see reds darker
    coralLight: '#f7d48f',
    persuadable: '#a9b4bf',
    purple: '#c9679f',
    orange: '#f2b544',
    navy: '#3f7ad9',
    palette: ['#2ec4b6', '#0f4c5c', '#f2b544', '#3f7ad9', '#c9679f', '#7a5195'],
    distribution: DISTRIBUTION
  }
};

// ── Legacy static exports (default scheme) ─────────────────────────────────
export const COLORS = {
  teal: SCHEMES.default.teal,
  tealDark: SCHEMES.default.tealDark,
  coral: SCHEMES.default.coral,
  persuadable: SCHEMES.default.persuadable,
  purple: SCHEMES.default.purple,
  orange: SCHEMES.default.orange,
  navy: SCHEMES.default.navy,
  ...CHROME
};

export const PALETTE = SCHEMES.default.palette;

export const DISTRIBUTION_COLORS = DISTRIBUTION;

// Compare mode: single-hue teal ramp for ORDERED dimensions (Age, Education,
// Income) — lightness-ordered, CVD-safe in every scheme.
export const COMPARE_RAMP = ['#5cc2b2', '#2fae9b', '#1a9081', '#127264', '#0c5348'];

// Compare mode: conventional party colors. Validated CVD-safe as a set
// (min simulated protan/deutan ΔE 13.0) — identical in every scheme.
// Matched by substring so raw values like "Democratic Party" also map.
export function partyColor(value) {
  const v = String(value || '').toLowerCase();
  if (v.includes('democrat')) return '#2a78d6';
  if (v.includes('republican')) return '#c93a3a';
  if (v.includes('independent')) return '#c98d00';
  return null;
}

// Common Chart.js tooltip style
export const tooltipStyle = {
  backgroundColor: 'rgba(255,255,255,0.92)',
  titleColor: COLORS.textPrimary,
  bodyColor: COLORS.textSecondary,
  borderColor: COLORS.border,
  borderWidth: 1,
  cornerRadius: 8,
  padding: 10,
  bodyFont: { size: 12 },
  titleFont: { size: 12, weight: 600 },
  displayColors: true,
  boxPadding: 4
};
